import { oc } from '@orpc/contract';
import { ACCOUNT_STATUSES, ROUTED_TO, TICKET_STATUSES, WAITING_ON } from '@lsc/domain';
import { z } from 'zod';
import { ticketHolder } from './presence.js';
export const ticketGetInput = z.object({ ticketId: z.uuid() });
const isoTs = z.string().nullable();
const text = z.string().nullable();
/** One row of the ticket's history: an append-only `ticket_event`, in the order it happened. */
export const ticketAttachment = z.object({
    id: z.uuid(),
    filename: z.string(),
    mime: z.string(),
    bytes: z.number().int(),
    source: z.enum(['desk', 'slack_thread']),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
});
export const ticketEventRow = z.object({
    id: z.number().int(),
    /** `import`, `status`, `assign`, `route`, `comment`, `call`, `sms`, ... (the table's open set). */
    type: z.string(),
    actorName: z.string().nullable(),
    /** Who did it, when it was a desk person; the desk offers Edit and Remove on your own notes. */
    actorProfileId: z.uuid().nullable(),
    payload: z.unknown().nullable(),
    /** Files a comment carries (from the attachment table, not the payload, so a Slack file counts too). */
    attachments: z.array(ticketAttachment).optional(),
    createdAt: z.string(),
});
/** Everything the ticket page shows, in one round trip. */
/** The AI fact sheet (spec 2026-10-24-ticket-summary-design.md): facts from the notes only, every field may be empty. */
export const ticketSummaryForm = z.object({
    ask: z.string(),
    done: z.array(z.string()),
    blockedOn: z.string(),
    promised: z.array(z.string()),
    nextStep: z.string(),
});
export const ticketSummary = z.object({
    /** The stored form, or null when none has been written yet. */
    form: ticketSummaryForm.nullable(),
    /** When the model wrote it. */
    at: z.string().nullable(),
    model: z.string().nullable(),
    /** Substantive events (notes, status changes, calls) after the summary: how stale it is. */
    newSince: z.number().int(),
    /** A run is queued or out to the model right now. */
    inFlight: z.boolean(),
    /** Whether the Update link does anything, with the words for when it does not. */
    refresh: z.union([z.object({ ok: z.literal(true) }), z.object({ ok: z.literal(false), message: z.string() })]),
    /** The newest run failed after the stored summary was written, so the summary may be behind. */
    lastFailed: z.boolean(),
});
export const ticketDetail = z.object({
    ticketId: z.uuid(),
    ticketNumber: z.string(),
    subject: z.string(),
    description: text,
    status: z.enum(TICKET_STATUSES),
    routedTo: z.enum(ROUTED_TO).nullable(),
    waitingOn: z.enum(WAITING_ON).nullable(),
    staffCategory: text,
    product: text,
    source: z.string(),
    priority: text,
    /** Null for an unlinked ticket (a mirrored row nobody has matched yet). */
    account: z
        .object({
        accountId: z.uuid(),
        label: z.string(),
        status: z.enum(ACCOUNT_STATUSES),
        mid: z.string().nullable(),
        processor: z.string().nullable(),
        /** Street, city, state in words (`placeWords`); null when the processor sent none. */
        place: z.string().nullable(),
        /** Other live tickets for the same store. */
        otherOpen: z.number().int(),
    })
        .nullable(),
    /** Support calls linked to this ticket (one per call, however many legs). */
    callCount: z.number().int(),
    /** The account contact the ticket is on, and whether they were on file before it (Jeff, 2026-10-25: say when the caller is known). */
    callerContact: z.object({ name: z.string(), role: text, onFileSince: z.string(), addedByTicket: z.boolean() }).nullable(),
    /** Other accounts that already know the caller's number, so a rep sees when the ticket may be on the wrong location. */
    numberElsewhere: z.array(z.object({ accountId: z.uuid(), label: z.string(), mid: text, place: text, contactName: text })),
    callerCompany: text,
    callerName: text,
    callerPhone: text,
    callerEmail: text,
    assigneeName: text,
    /** The desk holder's profile id (null for a Slack list team). The takeover names who it comes from. */
    assignedTo: z.uuid().nullable(),
    holders: z.array(ticketHolder),
    loggedByName: text,
    closedByName: text,
    createdAt: z.string(),
    lastActivityAt: isoTs,
    resolvedAt: isoTs,
    closedAt: isoTs,
    resolution: text,
    closeReason: text,
    rootCause: text,
    /** `slack:<item id>` for a row mirrored from the Slack list. */
    legacyRef: text,
    /** The ticket's thread in #support-tickets, when the desk posted one. */
    slackThreadTs: text,
    /** A Dialpad call is linked (the capture came off a call card); a hand-logged ticket says so instead. */
    fromCall: z.boolean(),
    /** The board stage it sits in (the list's label, else from status). */
    stage: z.string(),
    /** YYYY-MM-DD, while the stage is Scheduled. */
    scheduledFor: z.string().nullable(),
    events: z.array(ticketEventRow),
    summary: ticketSummary,
    /** Slack user id to name, for the `<@U…>` tags inside comment text. */
    slackNames: z.record(z.string(), z.string()),
});
export const ticketGet = oc.route({ method: 'POST', path: '/tickets/get', summary: 'One ticket with its account, people and history' }).input(ticketGetInput).output(ticketDetail.nullable());
//# sourceMappingURL=ticket-detail.js.map